import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { connectGithub, disconnectGithub, getGithubStatus } from "../lib/api";
import { Github, Lock, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getGithubStatus().then(setStatus);
  }, []);

  const onConnect = async () => {
    if (!token.trim()) {
      toast.error("Paste your GitHub token");
      return;
    }
    setBusy(true);
    try {
      const s = await connectGithub(token.trim());
      setStatus(s);
      setToken("");
      toast.success("Reconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid token");
    } finally {
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    setBusy(true);
    try {
      await disconnectGithub();
      const s = await getGithubStatus();
      setStatus(s);
      toast.success("Disconnected");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-8 py-12">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">Settings</span>
        <h1 className="text-3xl tracking-tight text-[#111827] mt-1.5">Account & integrations</h1>

        <div className="trace-card p-6 mt-8" data-testid="settings-github-card">
          <div className="flex items-center gap-2">
            <Github size={16} />
            <span className="font-medium">GitHub</span>
          </div>
          {status?.connected ? (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status.avatar_url && (
                  <img
                    src={status.avatar_url}
                    alt=""
                    className="w-9 h-9 rounded-full border border-[#e5e7eb]"
                  />
                )}
                <div>
                  <div className="text-sm text-[#111827]">{status.name || status.github_login}</div>
                  <div className="text-xs text-[#6b7280] font-mono">@{status.github_login}</div>
                </div>
              </div>
              <button
                data-testid="settings-disconnect"
                onClick={onDisconnect}
                disabled={busy}
                className="px-3 py-2 rounded-md bg-white border border-[#e5e7eb] text-sm hover:bg-[#f7f8fa] transition inline-flex items-center gap-1.5"
              >
                <LogOut size={14} /> Disconnect
              </button>
            </div>
          ) : (
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]"
                />
                <input
                  data-testid="settings-pat-input"
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="ghp_..."
                  className="w-full font-mono text-sm pl-9 pr-3 py-2.5 rounded-md bg-[#f7f8fa] border border-[#e5e7eb] outline-none focus:border-indigo-400 transition"
                />
              </div>
              <button
                data-testid="settings-connect"
                onClick={onConnect}
                disabled={busy}
                className="px-4 py-2.5 rounded-md bg-[#111827] text-white text-sm hover:bg-[#374151] transition disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                Connect
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-[#9ca3af]">
          Trace · Local-first project memory · v0.1
        </div>
      </div>
    </AppShell>
  );
}
